import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LinkOffOutlinedIcon from "@mui/icons-material/LinkOffOutlined";
import LockOpenOutlinedIcon from "@mui/icons-material/LockOpenOutlined";
import DrawOutlinedIcon from "@mui/icons-material/DrawOutlined";
import { formatDisplayDateTime } from "utils/dateFormat";
import { toast } from "react-toastify";
import {
  downloadUnencryptedVisaDocumentPdf,
  openVisaDocumentPdf,
  useCompleteVisaDocument,
  useRevokeVisaDocument,
  useSendVisaDocument,
  useVisaDocumentStatuses,
} from "utils/visaDocumentService";
import { useMaraAgents } from "utils/maraService";
import { useBankAccounts } from "utils/bankAccountService";
import CopySigningLinkButton from "./CopySigningLinkButton";
import SendSignatureRequestDialog from "./SendSignatureRequestDialog";
import CompleteAdminDocumentDialog from "./CompleteAdminDocumentDialog";
import DocumentSignaturesDialog from "./DocumentSignaturesDialog";
import PdfProtectionNote from "components/Documents/PdfProtectionNote";
import ConfirmDialog from "components/Modals/ConfirmDialog";

const STATUS_META = {
  SENT: { label: "Awaiting signature", color: "warning" },
  CLIENT_SIGNED: { label: "Needs completion", color: "info" },
  COMPLETED: { label: "Completed", color: "success" },
  REVOKED: { label: "Link revoked", color: "error" },
  // Only reachable on links sent while they still carried a 12 hour deadline; nothing sets it now.
  EXPIRED: { label: "Link expired", color: "error" },
};

const getStatusMeta = (status) => STATUS_META[status] || { label: "Not sent", color: "default" };

const DocumentCard = ({
  record,
  visaId,
  onSend,
  onResend,
  onComplete,
  onRevoke,
  onViewSignatures,
  sending,
  completing,
  revoking,
}) => {
  const [downloading, setDownloading] = useState(false);
  const statusMeta = getStatusMeta(record.status);
  const isCompleted = record.status === "COMPLETED";
  const isSent = record.status === "SENT" || record.status === "CLIENT_SIGNED";
  const needsAdminComplete = record.status === "CLIENT_SIGNED";
  const hasBeenSentBefore = Boolean(record.status);
  // Only a link the client has not acted on can be withdrawn; once they submit there is nothing
  // left to take back.
  const canRevoke = record.status === "SENT";

  const isProtected = Boolean(record.pdfProtection) && record.pdfProtection !== "NONE";

  const handleDownloadUnencrypted = async () => {
    setDownloading(true);
    try {
      await downloadUnencryptedVisaDocumentPdf(visaId, record.templateId, record.title);
    } catch (err) {
      toast.error(err?.message || "Failed to prepare the unencrypted copy");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownload = async (options) => {
    setDownloading(true);
    try {
      await openVisaDocumentPdf(visaId, record.templateId, options);
    } catch (err) {
      toast.error(err?.response?.data?.errorMessage || "Failed to open document");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Paper variant="outlined" className="border-app-border p-3.5 rounded-xl">
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: isCompleted ? "success.light" : "action.hover",
            color: isCompleted ? "success.dark" : "text.secondary",
          }}
        >
          {isCompleted ? (
            <CheckCircleIcon fontSize="small" />
          ) : (
            <DescriptionOutlinedIcon fontSize="small" />
          )}
        </Box>

        <Box className="min-w-0 flex-1">
          <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography variant="body2" fontWeight={700}>
              {record.title}
            </Typography>
            <Chip
              size="small"
              label={statusMeta.label}
              color={statusMeta.color}
              variant={isCompleted ? "filled" : "outlined"}
              sx={{ height: 20, fontSize: 11 }}
            />
          </Stack>
          {record.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              className="block"
              sx={{ mt: 0.25 }}
            >
              {record.description}
            </Typography>
          )}

          {isSent && (
            <Typography
              variant="caption"
              color="text.secondary"
              className="block"
              sx={{ mt: 0.75 }}
            >
              Sent to {record.sentTo || "client"} on {formatDisplayDateTime(record.sentAt)}. The
              link stays valid until they submit it.
            </Typography>
          )}

          {record.status === "REVOKED" && (
            <Typography
              variant="caption"
              color="text.secondary"
              className="block"
              sx={{ mt: 0.75 }}
            >
              The link sent to {record.sentTo || "the client"} no longer opens. Resend to issue a
              new one.
            </Typography>
          )}

          {isCompleted && (
            <Typography
              variant="caption"
              color="text.secondary"
              className="block"
              sx={{ mt: 0.75 }}
            >
              Completed on {formatDisplayDateTime(record.completedAt)}
            </Typography>
          )}

          {record.isPreviousCopy && (
            <Typography
              variant="caption"
              color="text.secondary"
              className="block"
              sx={{ mt: 0.75 }}
            >
              Previous copy available — awaiting new submission from client.
            </Typography>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.25 }}>
            <Button
              size="small"
              variant={hasBeenSentBefore ? "outlined" : "contained"}
              startIcon={
                hasBeenSentBefore ? (
                  <ReplayOutlinedIcon fontSize="small" />
                ) : (
                  <SendOutlinedIcon fontSize="small" />
                )
              }
              onClick={() => (hasBeenSentBefore ? onResend(record) : onSend(record))}
              disabled={sending}
            >
              {hasBeenSentBefore ? "Resend" : "Send to client"}
            </Button>
            {needsAdminComplete && (
              <Button
                size="small"
                variant="contained"
                onClick={() => onComplete(record)}
                disabled={completing}
              >
                Complete document
              </Button>
            )}
            {canRevoke && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<LinkOffOutlinedIcon fontSize="small" />}
                onClick={() => onRevoke(record)}
                disabled={revoking}
              >
                Revoke link
              </Button>
            )}
            <CopySigningLinkButton url={record.signingUrl} />
            {record.hasSignedPdf && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                onClick={() => handleDownload()}
                disabled={downloading}
              >
                {record.isPreviousCopy
                  ? "Download previous copy"
                  : needsAdminComplete
                  ? "Download client's submission"
                  : "Download filled copy"}
              </Button>
            )}
            {record.hasSignedPdf && isProtected && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<LockOpenOutlinedIcon fontSize="small" />}
                onClick={handleDownloadUnencrypted}
                disabled={downloading}
              >
                Download unencrypted
              </Button>
            )}
            {record.signatureCount > 0 && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DrawOutlinedIcon fontSize="small" />}
                onClick={() => onViewSignatures(record)}
              >
                Check signatures ({record.signatureCount})
              </Button>
            )}
            {record.hasLegacySignedPdf && (
              <Button
                size="small"
                variant="text"
                startIcon={<FileDownloadOutlinedIcon fontSize="small" />}
                onClick={() => handleDownload({ earlier: true })}
                disabled={downloading}
              >
                Earlier copy
              </Button>
            )}
          </Stack>
          {record.hasSignedPdf && (
            <Box sx={{ mt: 1 }}>
              <PdfProtectionNote
                protection={record.pdfProtection}
                password={record.pdfOwnerPassword}
              />
            </Box>
          )}
        </Box>
      </Stack>
    </Paper>
  );
};

const SignatureDocumentsPanel = ({ visaId, client }) => {
  const { data: documents = [], isFetching } = useVisaDocumentStatuses(visaId);
  const [sendTarget, setSendTarget] = useState(null);
  const [completeTarget, setCompleteTarget] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [signaturesTarget, setSignaturesTarget] = useState(null);

  // Both dialogs hold only which template they are about (plus the intent to resend), never a copy
  // of its row: statuses refetch while a dialog is open, and a snapshot taken on open would keep
  // showing the fields and signature the document had before it was last saved.
  const sendRecord = documents.find((record) => record.templateId === sendTarget?.templateId);
  const completeRecord = documents.find(
    (record) => record.templateId === completeTarget?.templateId
  );
  const revokeRecord = documents.find((record) => record.templateId === revokeTarget?.templateId);
  const signaturesRecord = documents.find(
    (record) => record.templateId === signaturesTarget?.templateId
  );

  const { data: maraAgents = [], isFetching: maraAgentsLoading } = useMaraAgents("VISA", {
    enabled: Boolean(sendRecord?.maraRequired),
  });

  const { data: bankAccounts = [], isFetching: bankAccountsLoading } = useBankAccounts({
    enabled: Boolean(sendRecord?.bankRequired),
  });

  const { mutate: sendDocument, isLoading: sending } = useSendVisaDocument({
    onSuccess: () => {
      toast.success("Document sent to client");
      setSendTarget(null);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.errorMessage || "Failed to send document");
    },
  });

  const { mutate: completeDocument, isLoading: completing } = useCompleteVisaDocument({
    onSuccess: () => {
      toast.success("Document completed");
      setCompleteTarget(null);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.errorMessage || "Failed to complete document");
    },
  });

  const { mutate: revokeDocument, isLoading: revoking } = useRevokeVisaDocument({
    onSuccess: () => {
      toast.success("Signing link revoked");
      setRevokeTarget(null);
    },
    onError: (err) => {
      toast.error(err?.response?.data?.errorMessage || "Failed to revoke the signing link");
    },
  });

  const handleConfirmSend = ({
    note,
    maraAgentId,
    bankAccountId,
    fromEmail,
    fieldValues,
    uppercaseValues,
  }) => {
    if (!sendTarget) return;
    sendDocument({
      visaId,
      templateId: sendTarget.templateId,
      message: note,
      maraAgentId,
      bankAccountId,
      fromEmail,
      fieldValues,
      uppercaseValues,
    });
  };

  const handleConfirmComplete = ({ fieldValues }) => {
    if (!completeTarget) return;
    completeDocument({ visaId, templateId: completeTarget.templateId, fieldValues });
  };

  const handleConfirmRevoke = () => {
    if (!revokeTarget) return;
    revokeDocument({ visaId, templateId: revokeTarget.templateId });
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Signable documents
        </Typography>
        {isFetching && <CircularProgress size={14} />}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Send these to the client to fill in and sign. A link stays valid until they submit it, so
        revoke it if it should no longer be usable.
      </Typography>

      {documents.length === 0 && !isFetching && (
        <Alert severity="info">
          No visa document templates uploaded yet. An admin with the right permission can add them
          via "Manage document templates" beside Add Visa.
        </Alert>
      )}

      <Stack spacing={1.5}>
        {documents.map((record) => (
          <DocumentCard
            key={record.templateId}
            record={record}
            visaId={visaId}
            onSend={(r) => setSendTarget({ templateId: r.templateId, resend: false })}
            onResend={(r) => setSendTarget({ templateId: r.templateId, resend: true })}
            onComplete={(r) => setCompleteTarget({ templateId: r.templateId })}
            onRevoke={(r) => setRevokeTarget({ templateId: r.templateId })}
            onViewSignatures={(r) => setSignaturesTarget({ templateId: r.templateId })}
            sending={sending}
            completing={completing}
            revoking={revoking}
          />
        ))}
      </Stack>

      <SendSignatureRequestDialog
        open={Boolean(sendTarget && sendRecord)}
        onClose={() => setSendTarget(null)}
        visaId={visaId}
        templateId={sendTarget?.templateId}
        documentTitle={sendRecord?.title || ""}
        recipientEmail={client?.email || ""}
        onSubmit={handleConfirmSend}
        loading={sending}
        resend={sendTarget?.resend}
        hasFilledCopy={sendRecord?.hasSignedPdf}
        maraRequired={sendRecord?.maraRequired}
        maraAgents={maraAgents}
        maraAgentsLoading={maraAgentsLoading}
        bankRequired={sendRecord?.bankRequired}
        bankAccounts={bankAccounts}
        bankAccountsLoading={bankAccountsLoading}
        adminFields={sendRecord?.adminPreSendFields || []}
        uppercaseSupported={Boolean(sendRecord?.uppercaseSupported)}
        uppercaseDefault={sendRecord?.uppercaseValues !== false}
      />
      <CompleteAdminDocumentDialog
        open={Boolean(completeTarget && completeRecord)}
        onClose={() => setCompleteTarget(null)}
        documentTitle={completeRecord?.title || ""}
        fields={completeRecord?.adminPostSignFields || []}
        assetUrls={completeRecord?.adminPostSignAssetUrls || {}}
        visaId={visaId}
        templateId={completeTarget?.templateId}
        canViewSubmission={Boolean(completeRecord?.hasSignedPdf)}
        uppercase={Boolean(completeRecord?.uppercaseValues)}
        onSubmit={handleConfirmComplete}
        loading={completing}
      />
      <DocumentSignaturesDialog
        open={Boolean(signaturesTarget && signaturesRecord)}
        onClose={() => setSignaturesTarget(null)}
        visaId={visaId}
        templateId={signaturesTarget?.templateId}
        documentTitle={signaturesRecord?.title || ""}
      />
      <ConfirmDialog
        open={Boolean(revokeTarget && revokeRecord)}
        title="Revoke signing link"
        message={`The link sent to ${revokeRecord?.sentTo || "the client"} for "${
          revokeRecord?.title || ""
        }" will stop working immediately. Anything you already filled in is kept, so you can resend a fresh link later.`}
        confirmLabel="Revoke link"
        confirmColor="error"
        loading={revoking}
        onConfirm={handleConfirmRevoke}
        onCancel={() => setRevokeTarget(null)}
      />
    </Box>
  );
};

export default SignatureDocumentsPanel;
